import { Fragment, useMemo, useState } from "react";
import {
  CARRIER_VALUE_TO_IMO,
  contractStatusHint,
  PINNACLE_AVAILABLE_CARRIERS,
  type ViewerCapabilities,
} from "@aam/shared";
import { Button } from "../ui/Button";
import { Label, Select, Textarea } from "../ui/Input";
import { ModalOverlay } from "../ui/Modal";
import { StatusBadge } from "../ui/StatusBadge";
import { ContractEventsTimeline, ContractUpstreamPanel } from "./ContractEventsTimeline";
import { ProviderContract } from "./types";

const CONTRACT_STATUSES = ["PENDING", "ACTIVE", "REJECTED", "NIGO"] as const;
type ContractStatus = typeof CONTRACT_STATUSES[number];

const CARRIER_LABELS = new Map(PINNACLE_AVAILABLE_CARRIERS.map((carrier) => [carrier.value, carrier.label]));

function fallbackLabel(value: string) {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function carrierLabel(value: string) {
  return CARRIER_LABELS.get(value) ?? fallbackLabel(value);
}

type ContractSortKey = "carrier" | "status" | "modified";

function compareContracts(
  left: ProviderContract,
  right: ProviderContract,
  sortKey: ContractSortKey,
  sortDirection: "asc" | "desc"
) {
  const direction = sortDirection === "asc" ? 1 : -1;
  const primary = (() => {
    switch (sortKey) {
      case "carrier":
        return carrierLabel(left.carrier).localeCompare(carrierLabel(right.carrier), undefined, { sensitivity: "base" });
      case "status":
        return left.status.localeCompare(right.status, undefined, { sensitivity: "base" });
      case "modified":
        return new Date(left.statusUpdatedAt).getTime() - new Date(right.statusUpdatedAt).getTime();
      default:
        return 0;
    }
  })();
  if (primary !== 0) return primary * direction;
  return left.id.localeCompare(right.id);
}

function upstreamLabel(contract: ProviderContract) {
  if (!contract.upstreamStatusName) return undefined;
  return contract.upstreamSource
    ? `${contract.upstreamStatusName} (${contract.upstreamSource})`
    : contract.upstreamStatusName;
}

function contractModifiedByLabel(contract: ProviderContract): string | undefined {
  if (contract.lastModifiedByUser?.email) {
    return `Changed by ${contract.lastModifiedByUser.email}`;
  }
  const statusEvent = contract.events.find(
    (event) => event.toStatus && event.actorUser?.email
  );
  if (statusEvent?.actorUser?.email) {
    return `Changed by ${statusEvent.actorUser.email}`;
  }
  return undefined;
}

interface ContractsTableProps {
  contracts: ProviderContract[];
  capabilities: ViewerCapabilities;
  latestSubmissionId?: string | null;
  addableCarriers?: Array<{ value: string; label: string }>;
  onAddContract?: (carrier: string) => Promise<void>;
  onDeleteContract?: (contractId: string) => Promise<void>;
  onUpdateStatus?: (contractId: string, status: string, comment: string) => Promise<void>;
  onOverrideStatus?: (contractId: string, status: string, comment: string) => Promise<void>;
  onPinContract?: (contractId: string, reason: string) => Promise<void>;
  onUnpinContract?: (contractId: string) => Promise<void>;
  showPortalHints?: boolean;
  saving?: string | null;
}

export function ContractsTable({
  contracts,
  capabilities,
  latestSubmissionId,
  addableCarriers = [],
  onAddContract,
  onDeleteContract,
  onUpdateStatus,
  onOverrideStatus,
  onPinContract,
  onUnpinContract,
  showPortalHints = false,
  saving,
}: ContractsTableProps) {
  const showUpstreamPanel = capabilities.viewMode === "full" && capabilities.canViewOperations;
  const showSourceColumn =
    capabilities.viewMode === "provider-intake" ||
    capabilities.canViewOperations ||
    capabilities.canManageContracts ||
    capabilities.canPinContract;
  const showUserActorColumn = capabilities.viewMode === "full" && showSourceColumn;
  const statusHandler = capabilities.canOverrideContractStatus ? onOverrideStatus : onUpdateStatus;
  const canChangeStatus = Boolean(statusHandler && capabilities.canUpdateContractStatus);
  const [expandedContractId, setExpandedContractId] = useState<string | null>(null);
  const [pendingPinContract, setPendingPinContract] = useState<{ contract: ProviderContract; reason: string } | null>(null);
  const [contractSortKey, setContractSortKey] = useState<ContractSortKey>("carrier");
  const [contractSortDirection, setContractSortDirection] = useState<"asc" | "desc">("asc");
  const [showAddContract, setShowAddContract] = useState(false);
  const [newCarrier, setNewCarrier] = useState(addableCarriers[0]?.value ?? "");
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    contract: ProviderContract;
    nextStatus: ContractStatus;
    comment: string;
  } | null>(null);
  const [pendingDeleteContract, setPendingDeleteContract] = useState<ProviderContract | null>(null);

  const sortedContracts = useMemo(
    () => [...contracts].sort((left, right) =>
      compareContracts(left, right, contractSortKey, contractSortDirection)
    ),
    [contracts, contractSortKey, contractSortDirection]
  );

  function toggleContractSort(key: ContractSortKey) {
    if (contractSortKey === key) {
      setContractSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setContractSortKey(key);
    setContractSortDirection("asc");
  }

  function sortIndicator(key: ContractSortKey) {
    if (contractSortKey !== key) return null;
    return contractSortDirection === "asc" ? "↑" : "↓";
  }

  function contractColumnCount(includeActions: boolean) {
    return (
      5 +
      (showSourceColumn ? 1 : 0) +
      (showUpstreamPanel ? 1 : 0) +
      (showUserActorColumn ? 1 : 0) +
      (includeActions ? 1 : 0)
    );
  }

  return (
    <>
      <section className="rounded-xl border border-border-default bg-surface-overlay p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">Provider Contracts ({contracts.length})</h2>
            <p className="mt-1 text-xs text-content-muted">
              All provider contract records for this agent, including upstream sync and manual updates.
            </p>
          </div>
          {capabilities.canAddContract && latestSubmissionId && onAddContract && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setNewCarrier(addableCarriers[0]?.value ?? "");
                setShowAddContract(true);
              }}
              disabled={addableCarriers.length === 0}
            >
              Add contract
            </Button>
          )}
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-content-secondary text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 pr-3" aria-sort={contractSortKey === "carrier" ? (contractSortDirection === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" onClick={() => toggleContractSort("carrier")} className="inline-flex items-center gap-1">
                    Carrier {sortIndicator("carrier") && <span className="text-accent">{sortIndicator("carrier")}</span>}
                  </button>
                </th>
                <th className="text-left py-2 pr-3" aria-sort={contractSortKey === "status" ? (contractSortDirection === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" onClick={() => toggleContractSort("status")} className="inline-flex items-center gap-1">
                    Status {sortIndicator("status") && <span className="text-accent">{sortIndicator("status")}</span>}
                  </button>
                </th>
                <th className="text-left py-2 pr-3">Upline</th>
                <th className="text-left py-2 pr-3">Notes</th>
                {showSourceColumn && <th className="text-left py-2 pr-3">Source</th>}
                <th className="text-left py-2 pr-3" aria-sort={contractSortKey === "modified" ? (contractSortDirection === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" onClick={() => toggleContractSort("modified")} className="inline-flex items-center gap-1">
                    Modified {sortIndicator("modified") && <span className="text-accent">{sortIndicator("modified")}</span>}
                  </button>
                </th>
                {showUpstreamPanel && <th className="text-left py-2 pr-3 w-8" />}
                {showUserActorColumn && <th className="text-left py-2 pr-3">User Actor</th>}
                {(capabilities.canDeleteContract ||
                  capabilities.canUpdateContractStatus ||
                  capabilities.canPinContract) && (
                  <th className="text-left py-2 pr-3">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedContracts.length === 0 ? (
                <tr>
                  <td colSpan={contractColumnCount(false)} className="py-4 text-content-muted">
                    No provider contracts for the latest submission yet.
                  </td>
                </tr>
              ) : sortedContracts.map((contract) => {
                const isExpanded = expandedContractId === contract.id;
                const hasActions =
                  capabilities.canDeleteContract ||
                  capabilities.canUpdateContractStatus ||
                  capabilities.canPinContract;
                const colSpan = contractColumnCount(hasActions);

                return (
                <Fragment key={contract.id}>
                <tr className="border-t border-border-subtle align-top">
                  <td className="py-2 pr-3 font-medium text-content-primary">{carrierLabel(contract.carrier)}</td>
                  <td className="py-2 pr-3">
                    {canChangeStatus ? (
                      <button
                        type="button"
                        onClick={() => setPendingStatusChange({
                          contract,
                          nextStatus: contract.status as ContractStatus,
                          comment: contract.notes ?? "",
                        })}
                        className="inline-flex text-left"
                      >
                        <StatusBadge
                          status={contract.status}
                          hint={showPortalHints ? contractStatusHint(contract.status) : undefined}
                          upstream={showUpstreamPanel ? upstreamLabel(contract) : undefined}
                          pinned={Boolean(contract.pinnedAt)}
                        />
                      </button>
                    ) : (
                      <StatusBadge
                        status={contract.status}
                        hint={showPortalHints ? contractStatusHint(contract.status) : undefined}
                        upstream={showUpstreamPanel ? upstreamLabel(contract) : undefined}
                        pinned={Boolean(contract.pinnedAt)}
                      />
                    )}
                  </td>
                  <td className="py-2 pr-3 max-w-[180px]">
                    <span
                      className="text-xs text-content-secondary line-clamp-2"
                      title={
                        [contract.upstreamUplineName, contract.upstreamUplineNpn]
                          .filter(Boolean)
                          .join(" · ") || undefined
                      }
                    >
                      {contract.upstreamUplineName ||
                        (contract.upstreamUplineNpn ? `NPN ${contract.upstreamUplineNpn}` : "—")}
                    </span>
                  </td>
                  <td className="py-2 pr-3 max-w-[200px]">
                    <span className="text-xs text-content-secondary line-clamp-2" title={contract.notes ?? undefined}>
                      {contract.notes || "—"}
                    </span>
                  </td>
                  {showSourceColumn && (
                    <td className="py-2 pr-3">
                      <span className="inline-flex rounded-full border border-border-default bg-surface-base px-2 py-0.5 text-[11px] text-content-secondary">
                        {contract.source}
                      </span>
                    </td>
                  )}
                  <td
                    className={`py-2 pr-3${contractModifiedByLabel(contract) ? " cursor-help" : ""}`}
                    title={contractModifiedByLabel(contract)}
                  >
                    {new Date(contract.statusUpdatedAt).toLocaleString()}
                  </td>
                  {showUpstreamPanel && (
                    <td className="py-2 pr-3">
                      {(contract.events.length > 0 || upstreamLabel(contract) || contract.pinnedAt) && (
                        <button
                          type="button"
                          className="text-xs text-accent hover:opacity-90"
                          onClick={() => setExpandedContractId(isExpanded ? null : contract.id)}
                        >
                          {isExpanded ? "Hide" : "Details"}
                        </button>
                      )}
                    </td>
                  )}
                  {showUserActorColumn && (
                    <td className="py-2 pr-3">{contract.lastModifiedByUser?.email ?? "—"}</td>
                  )}
                  {hasActions && (
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {capabilities.canPinContract && onPinContract && !contract.pinnedAt && (
                          <Button size="sm" variant="secondary" onClick={() => setPendingPinContract({ contract, reason: "" })}>
                            Pin
                          </Button>
                        )}
                        {capabilities.canPinContract && onUnpinContract && contract.pinnedAt && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={saving === contract.id}
                            onClick={() => onUnpinContract(contract.id)}
                          >
                            Unpin
                          </Button>
                        )}
                        {capabilities.canDeleteContract && onDeleteContract && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setPendingDeleteContract(contract)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                {isExpanded && showUpstreamPanel && (
                  <tr className="border-t border-border-subtle bg-surface-base/40">
                    <td colSpan={colSpan} className="py-3 px-2 space-y-3">
                      <ContractUpstreamPanel contract={contract} />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-content-muted mb-2">Event timeline</p>
                        <ContractEventsTimeline events={contract.events} />
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {showAddContract && onAddContract && (
        <ModalOverlay onClose={() => setShowAddContract(false)} ariaLabel="Attach a carrier contract">
            <h2 className="text-xl font-semibold text-content-primary mb-4">Attach a carrier contract</h2>
            <Label className="mb-5 block">
              Carrier
              <Select
                className="mt-1.5"
                value={newCarrier}
                onChange={(event) => setNewCarrier(event.target.value)}
              >
                {addableCarriers.map((carrier) => (
                  <option key={carrier.value} value={carrier.value}>{carrier.label}</option>
                ))}
              </Select>
            </Label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowAddContract(false)} disabled={saving === "add-contract"}>
                Cancel
              </Button>
              <Button
                disabled={!newCarrier || saving === "add-contract"}
                onClick={async () => {
                  await onAddContract(newCarrier);
                  setShowAddContract(false);
                }}
              >
                {saving === "add-contract" ? "Adding..." : "Add contract"}
              </Button>
            </div>
        </ModalOverlay>
      )}

      {pendingDeleteContract && onDeleteContract && (
        <ModalOverlay onClose={() => setPendingDeleteContract(null)} ariaLabel={`Delete ${carrierLabel(pendingDeleteContract.carrier)} contract`}>
            <h2 className="text-xl font-semibold text-content-primary mb-2">{carrierLabel(pendingDeleteContract.carrier)}</h2>
            <p className="text-sm text-content-secondary mb-5">This removes the contract record from the agent.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingDeleteContract(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={saving === pendingDeleteContract.id}
                onClick={async () => {
                  await onDeleteContract(pendingDeleteContract.id);
                  setPendingDeleteContract(null);
                }}
              >
                {saving === pendingDeleteContract.id ? "Deleting..." : "Delete contract"}
              </Button>
            </div>
        </ModalOverlay>
      )}

      {pendingStatusChange && statusHandler && (
        <ModalOverlay onClose={() => setPendingStatusChange(null)} ariaLabel={`Change ${carrierLabel(pendingStatusChange.contract.carrier)} contract status`}>
            <h2 className="text-xl font-semibold text-content-primary mb-2">{carrierLabel(pendingStatusChange.contract.carrier)}</h2>
            {capabilities.canOverrideContractStatus && (
              <p className="text-sm text-content-secondary mb-4">Admin override bypasses upstream sync until unpinned.</p>
            )}
            <Label className="mb-4 block">
              New status
              <Select
                className="mt-1.5"
                value={pendingStatusChange.nextStatus}
                onChange={(event) =>
                  setPendingStatusChange((current) =>
                    current ? { ...current, nextStatus: event.target.value as ContractStatus } : current
                  )
                }
              >
                {CONTRACT_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Select>
            </Label>
            <Label className="mb-4 block">
              Comment
              <Textarea
                className="mt-1.5 min-h-[88px]"
                value={pendingStatusChange.comment}
                onChange={(event) =>
                  setPendingStatusChange((current) =>
                    current ? { ...current, comment: event.target.value } : current
                  )
                }
                rows={3}
                placeholder="Optional note about this status change"
              />
            </Label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingStatusChange(null)}>Cancel</Button>
              <Button
                disabled={pendingStatusChange.nextStatus === pendingStatusChange.contract.status || saving === pendingStatusChange.contract.id}
                onClick={async () => {
                  await statusHandler(
                    pendingStatusChange.contract.id,
                    pendingStatusChange.nextStatus,
                    pendingStatusChange.comment
                  );
                  setPendingStatusChange(null);
                }}
              >
                {saving === pendingStatusChange.contract.id ? "Saving..." : "Confirm change"}
              </Button>
            </div>
        </ModalOverlay>
      )}

      {pendingPinContract && onPinContract && (
        <ModalOverlay onClose={() => setPendingPinContract(null)} ariaLabel={`Pin ${carrierLabel(pendingPinContract.contract.carrier)} contract`}>
            <h2 className="text-xl font-semibold text-content-primary mb-2">
              Pin {carrierLabel(pendingPinContract.contract.carrier)}
            </h2>
            <p className="text-sm text-content-secondary mb-4">
              Pinned contracts ignore upstream sync status updates until unpinned.
            </p>
            <Label className="mb-4 block">
              Reason (optional)
              <Textarea
                className="mt-1.5 min-h-[88px]"
                value={pendingPinContract.reason}
                onChange={(event) =>
                  setPendingPinContract((current) =>
                    current ? { ...current, reason: event.target.value } : current
                  )
                }
                rows={3}
                placeholder="Why is this contract pinned?"
              />
            </Label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingPinContract(null)}>Cancel</Button>
              <Button
                disabled={saving === pendingPinContract.contract.id}
                onClick={async () => {
                  await onPinContract(pendingPinContract.contract.id, pendingPinContract.reason);
                  setPendingPinContract(null);
                }}
              >
                {saving === pendingPinContract.contract.id ? "Pinning..." : "Pin contract"}
              </Button>
            </div>
        </ModalOverlay>
      )}
    </>
  );
}

export { CARRIER_VALUE_TO_IMO, PINNACLE_AVAILABLE_CARRIERS };
